import { createRouter, createWebHistory } from 'vue-router';
import { useAuthFlowStore } from '@Stores/authFlow.store';
import { createRoutes } from '@Router/routes';
import { getBasePath } from '@Src/mfe.config';
import { redirectToNotFound } from 'host/mfeBridge';
import { resolveAuthQueryParam } from '@Router/resolveAuthQueryParam';
import { redirectToSessionExpired } from '@Utils/sessionExpired';

const basePath = getBasePath() ?? '/';
const router = createRouter({
    history: createWebHistory(),
    routes: createRoutes(basePath)
});

// Routes here are outside any flow sequence (bootstrap and terminal/error
// screens), so the afterEach guard skips persisting them as the current flow
// position. Step-up flow routes (KBA, IDV, disable-vpn) are deliberately NOT
// in this set: they are FLOW_DEFINITIONS steps, and setCurrentRoute must run
// for them so the store's flow cursor tracks navigation and resume works.
const DEVICE_AUTH_ROUTES = new Set(['bootstrap', 'verify-device', 'access-denied', 'device-throttled', 'general-error', 'pause']);
router.afterEach((to) => {
    if (DEVICE_AUTH_ROUTES.has(to.name as string)) {
        return;
    }
    const authFlowStore = useAuthFlowStore();
    authFlowStore.setCurrentRoute(to.name as string);
});

router.beforeEach(async (to, from, next) => {
    const requiresClientIdInQuery = Boolean(to.meta?.requiresClientIdInQuery);

    if (!requiresClientIdInQuery) {
        next();
        return;
    }

    if (to.query.temporarySessionId) {
        next();
        return;
    }

    if (to.query.clientId) {
        next();
        return;
    }

    const { resolved, deviceAuthEnabled, sessionExpired } = await resolveAuthQueryParam();
    if (resolved) {
        next({ name: to.name, query: { ...to.query, ...resolved } });
        return;
    }

    // The session behind the cookie is gone. Restarting it is the way out, not a 404 page.
    // Like redirectToNotFound below, the host navigates away and unmounts this MFE.
    if (sessionExpired) {
        redirectToSessionExpired();
        return;
    }

    // DS-3208 — both bootstrap paths failed. Most likely cause: the company has neither V0
    // device-auth enabled (so getMobileSession is blocked at the BFF) nor a resolved
    // CertifidClientId on the request recipient (so getCurrentClientId can't find one on the
    // activity session). The full fix lives in the request-creation pipelines (Tier 2 — see
    // DS-3208 ticket for the codepaths). Log loud so we can correlate against the warehouse
    // stuck-record set and confirm Tier 2 drives this to zero.
    //
    // PR Buddy feedback: surface `deviceAuthEnabled` (not `requiresClientIdInQuery`, which is
    // always `true` by the time we reach this branch) so Datadog can filter total-failure
    // events by V0 mode vs. legacy mode and measure the Tier 1b BFF change's effect
    // independently.
    console.error(
        '[Auth MFE] Both getMobileSession and getCurrentClientId failed for protected route — redirecting to not-found (DS-3208).',
        { route: to.name, deviceAuthEnabled },
    );
    redirectToNotFound();
});

export default router;
